/**
 * FlowSummary
 * -----------
 * Carries the choices already made through the rest of the purchase.
 *
 * Once a doctor is chosen the client should not have to remember who, and once
 * a service is chosen they should not have to remember which — least of all on
 * the screen that finally asks for money. Each step shows what is already
 * settled, so the payment page is a confirmation rather than a leap of faith.
 *
 * Renders nothing when nothing is settled yet.
 */
import { Box, Stack, Typography, Chip } from '@mui/material';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import PersonIcon from '@mui/icons-material/Person';
import { localized } from '@/utils/localizedContent';

export default function FlowSummary({ consultation, lang, t, sx }) {
  const physician = consultation?.physicianName;
  const service = localized(consultation?.serviceName, lang);
  if (!physician && !service) return null;

  return (
    <Box
      sx={{
        px: 1.75,
        py: 1.5,
        mb: 2,
        borderRadius: 3,
        background: 'rgba(192,167,134,.10)',
        border: '1px solid rgba(192,167,134,.38)',
        ...sx,
      }}
    >
      <Stack spacing={1}>
        {physician && (
          <Row
            icon={<PersonIcon sx={{ fontSize: 17 }} />}
            label={t('flowSummary.physician')}
            value={physician}
            extra={consultation?.autoAssigned ? t('flowSummary.autoAssigned') : null}
          />
        )}
        {service && (
          <Row
            icon={<MedicalInformationIcon sx={{ fontSize: 17 }} />}
            label={t('flowSummary.service')}
            value={service}
          />
        )}
      </Stack>
    </Box>
  );
}

function Row({ icon, label, value, extra }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box sx={{ color: 'secondary.dark', display: 'flex' }}>{icon}</Box>
      <Typography
        sx={{
          flex: 'none',
          fontSize: '.68rem',
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          fontWeight: 700,
          color: 'secondary.dark',
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ flex: 1, minWidth: 0, fontWeight: 700 }} noWrap>
        {value}
      </Typography>
      {extra && <Chip size="small" variant="outlined" label={extra} />}
    </Stack>
  );
}
